import { NFCSignedContent } from '../../../shared/user/cashier';

export interface CardData {
  attendeeId: number;
  conventionId: number;
  issuance: number;
  timestamp: string;
  password: number;
  uuid: string;
  signature: string;
}

export enum NFCTagInterfaceType {
  HTTP = 'http',
  USBSerial = 'usbserial',
}

export enum NFCTagInterfaceManagerStatus {
  Ready = 'ready',
  Error = 'error',
}

export abstract class NFCTagInterfaceBase {
  public info?: SerialPortInfo;
  public port?: SerialPort;
  public httpAddress?: string;
  public connected = false;
  public originalDeviceUID = '';

  public abstract init(): void;
  public abstract dispose(): void;
  public abstract reset(): Promise<{ success: boolean }>;
  public abstract checkCardBlank(uuid: string): Promise<{ success: boolean; status: string }>;
  public abstract writeCard(cardData: CardData): Promise<{ success: boolean; status: string }>;
  public abstract setPassword(uuid: string, password: number): Promise<{ success: boolean }>;
  public abstract getUUID(): Promise<{ success: boolean; uuid: string }>;
  public abstract readCard(
    uuid: string,
    password: number,
  ): Promise<{ success: boolean; card?: CardData }>;

  // Event handling methods
  public abstract startEventSource(
    onMessage: (event: string) => void,
    onError: (status: string) => void,
    onOpen: (status: string) => void,
  ): () => void;
}

export async function validateSignature(cardData: {
  card: {
    attendeeId: number;
    conventionId: number;
    issuance: number;
    timestamp: string;
    signature: string;
  };
}): Promise<boolean> {
  // Create the signed content
  const signedContent: NFCSignedContent = {
    userId: cardData.card.attendeeId,
    conventionId: cardData.card.conventionId,
    issuanceCount: cardData.card.issuance,
    timestamp: cardData.card.timestamp,
  };

  const publicKeyResponse = await api.getNfcPublicKey();

  // Convert JWK to CryptoKey
  const publicKey = await window.crypto.subtle.importKey(
    'jwk',
    publicKeyResponse,
    {
      name: 'ECDSA',
      namedCurve: 'P-256',
    },
    false,
    ['verify'],
  );

  // Convert signature from base64 to ArrayBuffer
  const signatureBuffer = base64ToUint8Array(cardData.card.signature);

  // Convert DER signature to raw format (r and s concatenated)
  const derSignature = new Uint8Array(signatureBuffer);
  // Skip DER header (0x30, length)
  let offset = 2;
  // Skip r header (0x02)
  offset += 1;
  // Get r length
  const rLength = derSignature[offset];
  if (rLength === undefined) {
    throw new Error('Invalid DER signature format');
  }

  offset += 1;
  // Get r value
  const r = derSignature.slice(offset, offset + rLength);
  offset += rLength;
  // Skip s header (0x02)
  offset += 1;
  // Get s length
  const sLength = derSignature[offset];
  if (sLength === undefined) {
    throw new Error('Invalid DER signature format');
  }

  offset += 1;
  // Get s value
  const s = derSignature.slice(offset, offset + sLength);

  // Create padded r and s values (32 bytes each)
  const paddedR = new Uint8Array(32);
  const paddedS = new Uint8Array(32);

  // Copy r and s values, taking only the rightmost 32 bytes if longer
  const rToCopy = r.length > 32 ? r.slice(-32) : r;
  const sToCopy = s.length > 32 ? s.slice(-32) : s;

  // Copy values, right-aligned in the padded arrays
  paddedR.set(rToCopy, 32 - rToCopy.length);
  paddedS.set(sToCopy, 32 - sToCopy.length);

  // Concatenate padded r and s
  const rawSignature = new Uint8Array(64);
  // Fixed size for P-256
  rawSignature.set(paddedR);
  rawSignature.set(paddedS, 32);

  // Sort the keys alphabetically for predictable signature
  const sortedContent = Object.keys(signedContent)
    .sort()
    .reduce((obj: Record<string, number | string>, key) => {
      obj[key] = signedContent[key as keyof NFCSignedContent];
      return obj;
    }, {});

  const signedString = JSON.stringify(sortedContent);

  // Convert the signed string to ArrayBuffer
  const messageBuffer = new TextEncoder().encode(signedString);

  // Verify the signature
  return await window.crypto.subtle.verify(
    {
      name: 'ECDSA',
      hash: { name: 'SHA-256' },
    },
    publicKey,
    rawSignature,
    messageBuffer,
  );
}

function base64ToUint8Array(base64String: string) {
  const binaryString = atob(base64String);
  return Uint8Array.from(binaryString, (char) => char.codePointAt(0) ?? 0);
}

export function parseNFCTagInterfaceUid(str?: string):
  | {
      type: NFCTagInterfaceType;
      address: string;
    }
  | undefined {
  if (str === undefined) {
    return;
  }

  const [typeRaw, address] = str.split(/:(.*)/s) as [string, string?];
  if (address === undefined || address.length === 0) {
    return;
  }

  let type: NFCTagInterfaceType | undefined;
  switch (typeRaw.toUpperCase()) {
    case 'HTTP': {
      type = NFCTagInterfaceType.HTTP;
      break;
    }

    case 'USBSERIAL': {
      type = NFCTagInterfaceType.USBSerial;
      break;
    }
  }

  if (type === undefined) {
    return;
  }

  return { type, address };
}
